import React, { useEffect, useState, useRef, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  MessageSquare, Send, Plus, Users, Inbox, SendHorizonal,
  ChevronRight, Pencil, Trash2, Globe
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketContext } from '../contexts/WebSocketContext';

// ── helpers ────────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
}

function Avatar({ name, size = 32 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-full bg-primary/20 text-[#333] font-bold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

// ── Thread view ────────────────────────────────────────────────────────────

function ThreadView({ thread, currentUserId, onReply, onClose }) {
  const bottomRef = useRef(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await onReply(replyText, thread[0]);
      setReplyText('');
    } finally {
      setSending(false);
    }
  };

  if (!thread?.length) return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground">
      <div className="text-center">
        <MessageSquare size={48} className="mx-auto mb-3 opacity-20" />
        <p>Välj ett meddelande för att se tråden</p>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {thread.map((msg) => {
          const isMe = msg.sender_id === currentUserId;
          return (
            <div key={msg.id} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
              {!isMe && <Avatar name={msg.sender_name} size={32} />}
              <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {!isMe && (
                  <span className="text-xs text-muted-foreground font-semibold px-1">{msg.sender_name}</span>
                )}
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  isMe ? 'bg-primary text-[#333] rounded-tr-sm' : 'bg-muted rounded-tl-sm'
                }`}>
                  {msg.content}
                </div>
                <span className="text-xs text-muted-foreground px-1">{fmtTime(msg.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply bar */}
      <form onSubmit={handleReply} className="border-t p-3 flex gap-2 items-end">
        <Textarea
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          placeholder="Svara..."
          rows={1}
          className="flex-1 resize-none min-h-[40px] max-h-32"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(e); }
          }}
        />
        <Button type="submit" size="sm" className="rounded-full px-4 shrink-0" disabled={sending || !replyText.trim()}>
          <Send size={16} />
        </Button>
      </form>
    </div>
  );
}

// ── Message list item ─────────────────────────────────────────────────────

function MsgItem({ msg, isSelected, onClick }) {
  const unread = !msg.is_read;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 rounded-lg transition-colors flex gap-3 items-start ${
        isSelected ? 'bg-primary/20' : 'hover:bg-muted/60'
      }`}
    >
      <Avatar name={msg.sender_name} size={36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'}`}>
            {msg.sender_name}
          </span>
          <span className="text-xs text-muted-foreground shrink-0">{fmtTime(msg.created_at)}</span>
        </div>
        <p className={`text-xs truncate ${unread ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
          {msg.content}
        </p>
        {unread && (
          <span className="inline-block w-2 h-2 rounded-full bg-primary mt-1" />
        )}
      </div>
    </button>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

const MessagesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'customer_admin';

  useEffect(() => {
    api.post?.('/org/onboarding/user-event', { event_key: 'messages_opened' })?.catch(() => {});
  }, []);

  const [activeBox, setActiveBox] = useState('inbox'); // 'inbox' | 'sent' | 'groups'
  const [messages, setMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);  // array of messages
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(true);

  // New message dialog
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({ recipientType: 'org', recipientId: '', content: '' });
  const [composeSending, setComposeSending] = useState(false);

  // Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', memberIds: [] });
  const [groupSaving, setGroupSaving] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const box = activeBox === 'groups' ? undefined : activeBox;
      const params = box ? `?box=${box}` : '';
      const res = await api.get(`/messages${params}`);
      setMessages(res.data);
    } catch {
      // silently ignore poll errors
    } finally {
      setLoading(false);
    }
  }, [activeBox]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/message-groups');
      setGroups(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchOrgUsers = useCallback(async () => {
    if (orgUsers.length) return;
    try {
      const res = await api.get('/users');
      setOrgUsers(res.data.filter(u => u.id !== user?.id));
    } catch { /* ignore */ }
  }, [orgUsers.length, user?.id]);

  useEffect(() => {
    setLoading(true);
    setSelectedThread(null);
    setSelectedGroupId(null);
    fetchMessages();
    fetchGroups();
  }, [activeBox]); // eslint-disable-line

  // Poll every 8s (fallback when WebSocket is unavailable)
  useEffect(() => {
    const interval = setInterval(fetchMessages, 8000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Live updates — refresh the list the moment a new message arrives, and keep
  // the sidebar unread badge cleared while the page is open.
  const { lastMessage, resetUnreadMessages } = useWebSocketContext();

  useEffect(() => {
    resetUnreadMessages();
  }, [resetUnreadMessages]);

  useEffect(() => {
    if (lastMessage?.type === 'new_message') {
      fetchMessages();
      resetUnreadMessages();
    }
  }, [lastMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = async (rootMsg) => {
    try {
      const res = await api.get(`/messages/thread/${rootMsg.id}`);
      setSelectedThread(res.data);
      // Mark root as read
      if (!rootMsg.is_read) {
        await api.put(`/messages/${rootMsg.id}/read`);
        setMessages(prev => prev.map(m => m.id === rootMsg.id ? { ...m, is_read: true } : m));
      }
    } catch {
      setSelectedThread([rootMsg]);
    }
  };

  const openGroupChat = async (group) => {
    setSelectedGroupId(group.id);
    setSelectedThread(null);
    try {
      const res = await api.get(`/messages?group_id=${group.id}`);
      setSelectedThread(res.data.slice().reverse()); // oldest first
    } catch {
      setSelectedThread([]);
    }
  };

  const handleReply = async (content, rootMsg) => {
    try {
      const payload = {
        content,
        recipient_type: rootMsg.recipient_type || 'org',
        recipient_id: rootMsg.recipient_id || null,
        thread_id: rootMsg.thread_id || rootMsg.id,
      };
      const res = await api.post('/messages', payload);
      setSelectedThread(prev => [...prev, res.data]);
    } catch {
      toast.error('Kunde inte skicka svar');
    }
  };

  const handleGroupReply = async (content) => {
    if (!selectedGroupId) return;
    try {
      const res = await api.post('/messages', {
        content,
        recipient_type: 'group',
        recipient_id: selectedGroupId,
      });
      setSelectedThread(prev => [...(prev || []), res.data]);
    } catch {
      toast.error('Kunde inte skicka meddelande');
    }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!composeForm.content.trim()) return;
    setComposeSending(true);
    try {
      await api.post('/messages', {
        content: composeForm.content,
        recipient_type: composeForm.recipientType,
        recipient_id: composeForm.recipientId || null,
      });
      toast.success('Meddelande skickat!');
      setComposeOpen(false);
      setComposeForm({ recipientType: 'org', recipientId: '', content: '' });
      fetchMessages();
    } catch {
      toast.error('Kunde inte skicka meddelande');
    } finally {
      setComposeSending(false);
    }
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) return;
    setGroupSaving(true);
    try {
      if (editingGroup) {
        const res = await api.put(`/message-groups/${editingGroup.id}`, {
          name: groupForm.name,
          member_ids: groupForm.memberIds,
        });
        setGroups(prev => prev.map(g => g.id === editingGroup.id ? res.data : g));
        toast.success('Grupp uppdaterad');
      } else {
        const res = await api.post('/message-groups', {
          name: groupForm.name,
          member_ids: groupForm.memberIds,
        });
        setGroups(prev => [...prev, res.data]);
        toast.success('Grupp skapad');
      }
      setGroupDialogOpen(false);
    } catch {
      toast.error('Kunde inte spara grupp');
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await api.delete(`/message-groups/${groupId}`);
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (selectedGroupId === groupId) { setSelectedGroupId(null); setSelectedThread(null); }
      toast.success('Grupp borttagen');
    } catch {
      toast.error('Kunde inte ta bort grupp');
    }
  };

  const openGroupDialog = (group = null) => {
    setEditingGroup(group);
    setGroupForm(group ? { name: group.name, memberIds: group.member_ids || [] } : { name: '', memberIds: [] });
    fetchOrgUsers();
    setGroupDialogOpen(true);
  };

  const toggleGroupMember = (uid) => {
    setGroupForm(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(uid)
        ? prev.memberIds.filter(id => id !== uid)
        : [...prev.memberIds, uid],
    }));
  };

  // Filter messages: only root messages (no thread_id) for the list
  const rootMessages = messages.filter(m => !m.thread_id);
  const unreadCount = rootMessages.filter(m => !m.is_read && m.sender_id !== user?.id).length;

  const NAV = [
    { id: 'inbox', label: 'Inkorg', icon: Inbox, badge: unreadCount || null },
    { id: 'sent', label: 'Skickade', icon: SendHorizonal },
    { id: 'groups', label: 'Grupper', icon: Users },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="messages-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Meddelanden</h1>
            <p className="text-muted-foreground mt-1">Kommunicera med teamet</p>
          </div>
          <Button
            className="rounded-full font-bold"
            onClick={() => { fetchOrgUsers(); setComposeOpen(true); }}
          >
            <Plus size={18} className="mr-2" />
            Nytt meddelande
          </Button>
        </div>

        {/* 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[70vh]">
          {/* Left panel */}
          <div className="flex flex-col border rounded-xl overflow-hidden bg-background">
            {/* Nav tabs */}
            <div className="flex border-b">
              {NAV.map(n => {
                const Icon = n.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => setActiveBox(n.id)}
                    className={`flex-1 py-3 text-xs font-bold flex flex-col items-center gap-0.5 transition-colors relative ${
                      activeBox === n.id ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon size={16} />
                    {n.label}
                    {n.badge ? (
                      <span className="absolute top-1.5 right-3 w-4 h-4 rounded-full bg-primary text-[#333] text-[10px] font-black flex items-center justify-center">
                        {n.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {loading ? (
                <p className="text-sm text-muted-foreground text-center py-8">Laddar...</p>
              ) : activeBox === 'groups' ? (
                <>
                  {isAdmin && (
                    <button
                      className="w-full text-left px-3 py-2 text-sm font-bold text-primary flex items-center gap-2 hover:bg-muted/60 rounded-lg"
                      onClick={() => openGroupDialog()}
                    >
                      <Plus size={16} /> Skapa ny grupp
                    </button>
                  )}
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">Inga grupper ännu.</p>
                  ) : groups.map(g => (
                    <div
                      key={g.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-muted/60 ${
                        selectedGroupId === g.id ? 'bg-primary/20' : ''
                      }`}
                      onClick={() => openGroupChat(g)}
                    >
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Users size={16} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{g.name}</p>
                        <p className="text-xs text-muted-foreground">{(g.member_ids || []).length} medlemmar</p>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <button className="p-1 hover:text-primary" onClick={() => openGroupDialog(g)}>
                            <Pencil size={13} />
                          </button>
                          <button className="p-1 hover:text-red-500" onClick={() => handleDeleteGroup(g.id)}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : rootMessages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {activeBox === 'inbox' ? 'Inga meddelanden i inkorgen.' : 'Du har inte skickat några meddelanden.'}
                </p>
              ) : (
                rootMessages.map(msg => (
                  <MsgItem
                    key={msg.id}
                    msg={msg}
                    isSelected={selectedThread?.[0]?.id === msg.id}
                    onClick={() => openThread(msg)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right panel — thread */}
          <div className="lg:col-span-2 border rounded-xl overflow-hidden bg-background flex flex-col">
            {/* Header */}
            {selectedThread?.length ? (
              <div className="border-b px-4 py-3 flex items-center gap-3">
                {selectedGroupId ? (
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <Users size={16} />
                  </div>
                ) : selectedThread[0]?.recipient_type === 'org' ? (
                  <Globe size={20} className="text-muted-foreground" />
                ) : (
                  <Avatar name={selectedThread[0]?.sender_name} size={32} />
                )}
                <div>
                  <p className="font-bold text-sm">
                    {selectedGroupId
                      ? groups.find(g => g.id === selectedGroupId)?.name || 'Gruppchat'
                      : selectedThread[0]?.recipient_type === 'org'
                      ? 'Hela organisationen'
                      : selectedThread[0]?.sender_name
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">{selectedThread.length} meddelande{selectedThread.length !== 1 ? 'n' : ''}</p>
                </div>
              </div>
            ) : null}

            {selectedGroupId ? (
              <ThreadView
                thread={selectedThread || []}
                currentUserId={user?.id}
                onReply={(content) => handleGroupReply(content)}
                onClose={() => { setSelectedGroupId(null); setSelectedThread(null); }}
              />
            ) : (
              <ThreadView
                thread={selectedThread}
                currentUserId={user?.id}
                onReply={handleReply}
                onClose={() => setSelectedThread(null)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">Nytt meddelande</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCompose} className="space-y-4">
            <div className="space-y-2">
              <Label>Skicka till</Label>
              <Select
                value={composeForm.recipientType}
                onValueChange={v => setComposeForm(prev => ({ ...prev, recipientType: v, recipientId: '' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Hela organisationen</SelectItem>
                  <SelectItem value="group">Grupp</SelectItem>
                  <SelectItem value="user">Specifik användare</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {composeForm.recipientType === 'group' && (
              <div className="space-y-2">
                <Label>Välj grupp</Label>
                <Select
                  value={composeForm.recipientId || undefined}
                  onValueChange={v => setComposeForm(prev => ({ ...prev, recipientId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Välj grupp" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {composeForm.recipientType === 'user' && (
              <div className="space-y-2">
                <Label>Välj mottagare</Label>
                <Select
                  value={composeForm.recipientId || undefined}
                  onValueChange={v => setComposeForm(prev => ({ ...prev, recipientId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Välj användare" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Meddelande *</Label>
              <Textarea
                value={composeForm.content}
                onChange={e => setComposeForm(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
                placeholder="Skriv ditt meddelande..."
                required
              />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setComposeOpen(false)}>
                Avbryt
              </Button>
              <Button type="submit" className="flex-1 rounded-full font-bold" disabled={composeSending}>
                {composeSending ? 'Skickar...' : (
                  <><Send size={16} className="mr-2" />Skicka</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Group dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">
              {editingGroup ? 'Redigera grupp' : 'Skapa grupp'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Gruppnamn *</Label>
              <Input
                value={groupForm.name}
                onChange={e => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="T.ex. Handläggare team A"
              />
            </div>

            <div className="space-y-2">
              <Label>Medlemmar</Label>
              <div className="max-h-52 overflow-y-auto border rounded-lg divide-y">
                {orgUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Laddar användare...</p>
                ) : orgUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40">
                    <Checkbox
                      id={`m-${u.id}`}
                      checked={groupForm.memberIds.includes(u.id)}
                      onCheckedChange={() => toggleGroupMember(u.id)}
                    />
                    <Label htmlFor={`m-${u.id}`} className="cursor-pointer flex-1">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{groupForm.memberIds.length} valda</p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setGroupDialogOpen(false)}>
                Avbryt
              </Button>
              <Button
                className="flex-1 rounded-full font-bold"
                onClick={handleSaveGroup}
                disabled={groupSaving || !groupForm.name.trim()}
              >
                {groupSaving ? 'Sparar...' : editingGroup ? 'Spara ändringar' : 'Skapa grupp'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MessagesPage;
